"use client";

import React, { useState, useEffect } from "react";
import { Bot, Cpu, Trophy, ArrowUpRight, Wrench, Sparkles, CircleCheck as CheckCircle2, ShieldCheck, ChevronRight, ArrowRight, ArrowDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface PathwayOption {
  id: string;
  stageNumber: string;
  stageTitle: {
    vi: string;
    en: string;
  };
  badge: {
    vi: string;
    en: string;
  };
  code: {
    vi: string;
    en: string;
  };
  title: {
    vi: string;
    en: string;
  };
  trackName: {
    vi: string;
    en: string;
  };
  equipment: {
    vi: string;
    en: string;
  };
  requirement?: {
    vi: string;
    en: string;
  };
  description: {
    vi: string;
    en: string;
  };
  highlights: {
    vi: string[];
    en: string[];
  };
  icon: "bot" | "cpu" | "trophy";
}
const copy = {
  tag: {
    vi: "Lộ Trình Robotics Chuẩn Hóa",
    en: "Standardized Robotics Roadmap"
  },
  title: {
    vi: "Con bạn đang ở đâu trên hành trình Robotics?",
    en: "Where is your child on the Robotics journey?"
  },
  subtitle: {
    vi: "Hệ thống phân tầng khoa học từ nền tảng mầm non đến đấu trường quốc tế, giúp phụ huynh định vị chính xác điểm xuất phát tối ưu cho con.",
    en: "A structured developmental continuum from early spatial building to competitive tournament teams, pinpointing your child's ideal starting point."
  },
  equipmentLabel: {
    vi: "Học cụ chủ đạo",
    en: "Core Equipment"
  },
  requirementLabel: {
    vi: "Điều kiện đầu vào",
    en: "Entry Requirement"
  },
  viewFullRoadmap: {
    vi: "Xem lộ trình đầy đủ",
    en: "View full pathway"
  },
  openLevelNotice: {
    vi: "Khởi đầu không cần kiến thức trước",
    en: "Open entry // No prerequisites needed"
  },
  progressionNote: {
    vi: "Hành trình nối tiếp 3 giai đoạn liên tục",
    en: "Continuous 3-Stage Developmental Pipeline"
  },
  stageLabel: {
    vi: "GIAI ĐOẠN",
    en: "STAGE"
  },
  nextStageLabel: {
    vi: "Tiến cấp tiếp theo",
    en: "Advances to next stage"
  }
};
const pathways: PathwayOption[] = [{
  id: "pathway-duplo",
  stageNumber: "01",
  stageTitle: {
    vi: "Khởi tạo Nền tảng",
    en: "Foundation & Spatial Logic"
  },
  badge: {
    vi: "4–6 TUỔI",
    en: "AGES 4–6"
  },
  code: {
    vi: "TRACK D1 → D4",
    en: "TRACK D1 → D4"
  },
  title: {
    vi: "Robotics 4–6 tuổi",
    en: "Robotics Ages 4–6"
  },
  trackName: {
    vi: "Duplo Foundation Track (D1 → D4)",
    en: "Duplo Foundation Track (D1 → D4)"
  },
  equipment: {
    vi: "LEGO Education Duplo construction kit",
    en: "LEGO Education Duplo construction kit"
  },
  description: {
    vi: "Xây dựng nền tảng cơ học thực hành và tư duy không gian trực quan. Học sinh làm quen với cấu trúc vật lý, hệ khớp nối và chuỗi logic vận hành đầu đời.",
    en: "Establishes a hands-on mechanical and spatial-logic foundation, engaging young builders with physical mechanisms, interlocking structures, and early logic."
  },
  highlights: {
    vi: ["Tư duy hình khối không gian", "Cơ học cấu trúc trực quan", "Thói quen tư duy kỹ thuật"],
    en: ["Spatial reasoning", "Hands-on mechanical structures", "Early engineering mindset"]
  },
  icon: "bot"
}, {
  id: "pathway-whalesbot",
  stageNumber: "02",
  stageTitle: {
    vi: "Tự hành & Thuật toán",
    en: "Autonomous Logic & Coding"
  },
  badge: {
    vi: "7+ TUỔI",
    en: "AGES 7+"
  },
  code: {
    vi: "TRACK HP1 → HP6",
    en: "TRACK HP1 → HP6"
  },
  title: {
    vi: "Robotics 7+ tuổi",
    en: "Robotics Ages 7+"
  },
  trackName: {
    vi: "WhalesBot AI Module 1S Track (HP1 → HP6)",
    en: "WhalesBot AI Module 1S Track (HP1 → HP6)"
  },
  equipment: {
    vi: "WhalesBot AI Module 1S Platform",
    en: "WhalesBot AI Module 1S Platform"
  },
  requirement: {
    vi: "Hoàn thành D4 hoặc đạt bài kiểm tra đầu vào",
    en: "Completed D4 or passed entrance assessment"
  },
  description: {
    vi: "Bước chuyển quan trọng từ cơ học kỹ thuật cơ sở (junior mechanical engineering) tiến sang chế tạo và lập trình robot nhiệm vụ tự hành tích hợp cảm biến.",
    en: "Progresses from junior mechanical engineering to autonomous mission robots, integrating sensor logic, algorithmic decision-making, and intelligent actuation."
  },
  highlights: {
    vi: ["Truyền động bánh răng & motor", "Lập trình thuật toán điều khiển", "Robot giải quyết nhiệm vụ"],
    en: ["Drive trains & motor sync", "Algorithmic control logic", "Mission-oriented robotics"]
  },
  icon: "cpu"
}, {
  id: "pathway-enjoy-ai",
  stageNumber: "03",
  stageTitle: {
    vi: "Đấu trường Quốc tế",
    en: "International Arena & Mastery"
  },
  badge: {
    vi: "NÂNG CAO",
    en: "ADVANCED"
  },
  code: {
    vi: "CAPSTONE / TOURNAMENT",
    en: "CAPSTONE / TOURNAMENT"
  },
  title: {
    vi: "Nâng cao / Enjoy AI",
    en: "Advanced / Enjoy AI Prep"
  },
  trackName: {
    vi: "Enjoy AI Exam Prep Capstone",
    en: "Enjoy AI Exam Prep Capstone"
  },
  equipment: {
    vi: "Tournament Arenas & Mechatronics Kits",
    en: "Tournament Arenas & Mechatronics Kits"
  },
  requirement: {
    vi: "Hoàn thành HP6",
    en: "Completed HP6"
  },
  description: {
    vi: "Lộ trình chuyên sâu riêng biệt dành cho đội tuyển tinh hoa tuyển chọn, tập trung tối ưu hóa chiến thuật đấu trường, firmware điều tốc và thi đấu quốc tế.",
    en: "A separate advanced pathway for a selected tournament squad mastering competitive arena tactics, high-precision mechatronics, and tournament strategy."
  },
  highlights: {
    vi: ["Đội tuyển thi đấu chọn lọc", "Chiến thuật đấu trường quốc tế", "Tối ưu hóa cơ điện tử"],
    en: ["Selected tournament squad", "International arena strategy", "High-precision mechatronics"]
  },
  icon: "trophy"
}];
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
export default function AgePrograms() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      } else {
        setLang("vi");
      }
    } catch {
      setLang("vi");
    }
    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomEvent);
    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomEvent);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);
  const renderIcon = (type: PathwayOption["icon"]) => {
    switch (type) {
      case "bot":
        return <Bot className="size-6 text-secondary" strokeWidth={1.75} />;
      case "cpu":
        return <Cpu className="size-6 text-secondary" strokeWidth={1.75} />;
      case "trophy":
        return <Trophy className="size-6 text-accent" strokeWidth={1.75} />;
    }
  };
  return <section id="where-is-your-child-robotics" data-nav="light" className="bg-background text-foreground py-20 lg:py-24 border-b border-border relative overflow-hidden">
      {/* Decorative technical ambient grid overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.025]" style={{
      backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
      backgroundSize: "32px 32px"
    }} />

      {/* Static Floating Decorative Mascot Visual */}
      <div aria-hidden="true" className="pointer-events-none select-none absolute top-4 right-4 sm:top-5 sm:right-6 lg:top-7 lg:right-10 xl:right-12 z-0 hidden sm:block">
        <div className="relative w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24">
          <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt={defaultMascot.alt} loading="lazy" className="w-full h-full object-contain pointer-events-none select-none drop-shadow-sm bg-transparent" />
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Lab Blueprint Spec Rail Header */}
        <div className="mb-10 lg:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-1.5 w-1.5 bg-secondary rounded-xs" />
            <div className="h-px w-16 bg-secondary/70" />
            <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              {copy.tag[lang]}
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-9">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                {copy.title[lang]}
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-3xl leading-relaxed">
                {copy.subtitle[lang]}
              </p>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <span className="inline-flex items-center gap-2 rounded-lg border border-secondary/40 bg-secondary/10 px-3.5 py-1.5 text-xs font-mono font-semibold text-foreground shadow-xs">
                <CheckCircle2 className="size-3.5 text-secondary" />
                <span>3 Lộ trình kế thừa</span>
              </span>
            </div>
          </div>
        </div>

        {/* Infographic Milestone Track Rail (Desktop & Tablet) */}
        <div className="mb-8 hidden md:block rounded-xl border border-border/80 bg-muted/30 p-4 lg:p-5 shadow-2xs backdrop-blur-xs">
          <div className="flex items-center justify-between gap-4">
            {pathways.map((item, idx) => {
            const isLast = idx === pathways.length - 1;
            const isAdvanced = item.id === "pathway-enjoy-ai";
            return <React.Fragment key={`stage-track-${item.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`size-9 rounded-lg font-mono text-xs font-bold flex items-center justify-center shrink-0 border shadow-xs ${isAdvanced ? "bg-accent text-accent-foreground border-accent" : "bg-secondary text-secondary-foreground border-secondary"}`}>
                      {item.stageNumber}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-mono font-bold tracking-wider uppercase text-muted-foreground">
                        {copy.stageLabel[lang]} {item.stageNumber} • {item.badge[lang]}
                      </div>
                      <div className="text-xs font-semibold text-foreground truncate">
                        {item.stageTitle[lang]}
                      </div>
                    </div>
                  </div>

                  {!isLast && <div className="flex-1 px-2 flex items-center gap-1.5">
                      <div className="h-0.5 flex-1 bg-gradient-to-r from-secondary/80 to-secondary/30 rounded-full" />
                      <div className="size-6 rounded-lg bg-card border border-border flex items-center justify-center text-secondary shrink-0 shadow-2xs">
                        <ArrowRight className="size-3.5" />
                      </div>
                      <div className="h-0.5 flex-1 bg-gradient-to-r from-secondary/30 to-secondary/80 rounded-full" />
                    </div>}
                </React.Fragment>;
          })}
          </div>
        </div>

        {/* Visual Infographic Progression Flow (Cards with Connectors) */}
        <div className="relative">
          {/* Continuous connector spine behind cards on Desktop */}
          <div aria-hidden="true" className="hidden lg:block pointer-events-none absolute top-1/2 left-8 right-8 -translate-y-1/2 h-0.5 z-0">
            <div className="w-full h-full border-t-2 border-dashed border-secondary/40" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch relative z-10">
            {pathways.map((item, i) => {
            const isAdvanced = item.id === "pathway-enjoy-ai";
            const isLast = i === pathways.length - 1;
            const pathwayHref = item.id === "pathway-duplo" ? "/RoboticsLearningPathwayPage#duplo-foundation-track" : item.id === "pathway-whalesbot" ? "/RoboticsLearningPathwayPage#whalesbot-programming-track" : item.id === "pathway-enjoy-ai" ? "/RoboticsLearningPathwayPage#enjoy-ai-pathway" : "/RoboticsLearningPathwayPage";
            return <div key={item.id} data-index={i} className="flex flex-col relative">
                  {/* Card Container */}
                  <div className={`group relative rounded-xl border bg-card p-6 sm:p-7 lg:p-8 flex flex-col justify-between transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg overflow-hidden h-full ${isAdvanced ? "border-accent/40 shadow-sm hover:border-accent" : "border-border shadow-xs hover:border-secondary"}`}>
                    {/* Precision Spec Top Rail */}
                    <div className={`absolute top-0 left-0 right-0 h-1.5 flex items-center ${isAdvanced ? "bg-accent" : "bg-secondary"}`}>
                      <div className="h-1.5 w-16 bg-primary" />
                    </div>

                    {/* Subtle blueprint coordinate background on card */}
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity" style={{
                  backgroundImage: "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
                  backgroundSize: "24px 24px"
                }} />

                    {/* Top Section: Stage Infographic Badges & Headings */}
                    <div className="relative z-10">
                      {/* Step Indicator Header within Card */}
                      <div className="flex items-center justify-between gap-3 mb-5">
                        <div className="flex items-center gap-2">
                          {/* Sequential Stage Badge */}
                          <span className={`inline-flex items-center gap-1.5 rounded-lg font-mono text-xs font-bold px-2.5 py-1 tracking-wider border shadow-2xs ${isAdvanced ? "bg-accent text-accent-foreground border-accent" : "bg-secondary text-secondary-foreground border-secondary"}`}>
                            <span>{copy.stageLabel[lang]}</span>
                            <span className="font-bold">{item.stageNumber}</span>
                          </span>

                          <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-mono font-bold uppercase tracking-wide ${isAdvanced ? "border-accent/40 bg-accent/10 text-accent-foreground dark:text-accent" : "border-secondary/40 bg-secondary/15 text-secondary-foreground"}`}>
                            <Sparkles className="size-3 text-secondary" />
                            {item.badge[lang]}
                          </span>
                        </div>

                        <div className="size-11 rounded-lg border border-border bg-muted/50 flex items-center justify-center shrink-0 shadow-2xs group-hover:border-secondary/50 transition-colors">
                          {renderIcon(item.icon)}
                        </div>
                      </div>

                      <div className="mb-2">
                        <span className="font-mono text-[11px] font-semibold text-muted-foreground uppercase tracking-wider block">
                          {item.code[lang]}
                        </span>
                        <h3 className="font-serif text-2xl lg:text-3xl font-bold text-foreground mt-1 tracking-tight">
                          {item.title[lang]}
                        </h3>
                      </div>

                      <div className="text-xs font-mono font-semibold text-primary mb-4 flex items-center gap-1.5">
                        <span className="size-1.5 rounded-xs bg-primary" />
                        <span>{item.trackName[lang]}</span>
                      </div>

                      <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                        {item.description[lang]}
                      </p>

                      {/* Highlights checklist */}
                      <div className="space-y-2 mb-6 pt-4 border-t border-border/80">
                        {item.highlights[lang].map((hl, hlIdx) => <div key={hlIdx} className="flex items-center gap-2 text-xs font-medium text-foreground">
                            <CheckCircle2 className="size-3.5 text-secondary shrink-0" />
                            <span>{hl}</span>
                          </div>)}
                      </div>

                      {/* Toolset & Requirement Specification Block */}
                      <div className="bg-muted/40 rounded-lg p-3.5 border border-border mb-6 space-y-3">
                        <div>
                          <span className="flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider text-muted-foreground">
                            <Wrench className="size-3 text-secondary" />
                            {copy.equipmentLabel[lang]}
                          </span>
                          <p className="font-mono text-xs font-semibold text-foreground mt-1">
                            {item.equipment[lang]}
                          </p>
                        </div>

                        <div>
                          <span className="flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider text-muted-foreground">
                            <ShieldCheck className="size-3 text-primary" />
                            {copy.requirementLabel[lang]}
                          </span>
                          <p className="text-xs font-medium mt-1 text-foreground">
                            {item.requirement ? <span className="text-primary font-semibold">
                                {item.requirement[lang]}
                              </span> : <span className="text-muted-foreground italic">
                                {copy.openLevelNotice[lang]}
                              </span>}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bottom CTA Action Button */}
                    <div className="relative z-10 pt-4 border-t border-border mt-auto">
                      <Button asChild variant="outline" className="w-full justify-between rounded-lg border-border hover:border-primary hover:bg-primary hover:text-primary-foreground font-semibold py-3 px-4 transition-[color,background-color,border-color,box-shadow] duration-200 group/btn">
                        <Link to={pathwayHref}>
                          <span>{copy.viewFullRoadmap[lang]}</span>
                          <ArrowUpRight className="size-4 ml-2 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {/* Desktop Between-Cards Directional Connector Pill */}
                  {!isLast && <div aria-hidden="true" className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 z-20 size-10 rounded-lg bg-card border-2 border-secondary shadow-md items-center justify-center text-secondary transition-transform hover:scale-110">
                      <ChevronRight className="size-5 stroke-[2.5]" />
                    </div>}

                  {/* Mobile & Tablet Vertical Connecting Pipeline Node */}
                  {!isLast && <div aria-hidden="true" className="lg:hidden flex flex-col items-center justify-center my-3 relative z-10">
                      <div className="h-3 w-0.5 bg-secondary/60" />
                      <div className="inline-flex items-center gap-2 rounded-lg border border-secondary/50 bg-secondary/15 px-3 py-1 font-mono text-[11px] font-semibold text-secondary-foreground shadow-2xs">
                        <span>{copy.nextStageLabel[lang]}</span>
                        <ArrowDown className="size-3.5 text-secondary animate-bounce" />
                      </div>
                      <div className="h-3 w-0.5 bg-secondary/60" />
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}